import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, money, errMsg } from "@/lib/api";
import { StatusPill, EmptyState, InlineLoader } from "@/components/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Banknote, Loader2, AlertCircle, CheckCircle2, Clock, Wallet, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

export default function PayoutRequest() {
  const [campaigns, setCampaigns] = useState(null);
  const [profile, setProfile] = useState(null);
  const [payouts, setPayouts] = useState([]);
  const [fees, setFees] = useState(null);
  const [identity, setIdentity] = useState(null);
  const [campaignId, setCampaignId] = useState("");
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const load = async () => {
    const [{ data }, idv] = await Promise.all([
      api.get("/organizer/dashboard"),
      api.get("/payouts/identity/status").then((r) => r.data).catch(() => null),
    ]);
    setCampaigns((data.campaigns || []).filter((c) => ["live", "approved", "completed"].includes(c.status)));
    setProfile(data.organizer_profile);
    setPayouts(data.payouts || []);
    setFees(data.fees || null);
    setIdentity(idv);
  };
  useEffect(() => { load().catch(() => setCampaigns([])); }, []);

  const selected = useMemo(() => (campaigns || []).find((c) => c.id === campaignId), [campaigns, campaignId]);
  const onboardingComplete = profile?.stripe_onboarding_complete;
  const feePct = fees?.applicable_fee_pct ?? 5;
  const amt = Number(amount) || 0;
  const feeAmount = Math.round(amt * feePct) / 100;
  const netAmount = Math.round((amt - feeAmount) * 100) / 100;

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!campaignId) return setError("Select a campaign.");
    if (!amount || Number(amount) < 1) return setError("Enter a valid amount.");
    setLoading(true);
    try {
      await api.post("/payouts/request", { campaign_id: campaignId, amount: Number(amount) });
      toast.success("Payout requested. It will enter review.");
      setAmount(""); setCampaignId("");
      await load();
    } catch (err) { setError(errMsg(err)); }
    finally { setLoading(false); }
  };

  if (!campaigns) return <InlineLoader label="Loading" />;

  return (
    <div className="mx-auto max-w-3xl">
      <h1 className="font-heading text-2xl font-extrabold tracking-tight text-ff-blue sm:text-3xl">Request a Payout</h1>
      <p className="mt-1 text-sm text-ff-medium">Payouts release through Stripe after review readiness and settlement.</p>

      {!onboardingComplete && (
        <Alert className="mt-6 border-ff-amber/30 bg-[#FFF7ED]" data-testid="payout-onboarding-warning">
          <Wallet className="h-4 w-4 text-ff-amber" />
          <AlertDescription className="text-ff-dark">
            Complete <Link to="/organizer/onboarding" className="font-semibold text-ff-amber underline">payout onboarding</Link> before requesting funds.
          </AlertDescription>
        </Alert>
      )}

      {identity && !identity.verified && (
        <Alert className="mt-4 border-ff-amber/30 bg-[#FFF7ED]" data-testid="payout-kyc-warning">
          <ShieldCheck className="h-4 w-4 text-ff-amber" />
          <AlertDescription className="text-ff-dark">
            Identity verification (KYC) is required before your first payout is <strong>released</strong>. You can still
            request now — <Link to="/organizer/onboarding" className="font-semibold text-ff-amber underline">verify your identity</Link> to unlock release.
          </AlertDescription>
        </Alert>
      )}

      <form onSubmit={submit} className="mt-6 rounded-2xl border border-ff-border bg-white p-8 shadow-sm" data-testid="payout-form">
        {error && <Alert variant="destructive" className="mb-4"><AlertCircle className="h-4 w-4" /><AlertDescription>{error}</AlertDescription></Alert>}
        {campaigns.length === 0 ? (
          <EmptyState icon={Banknote} title="No eligible campaigns" message="You need a live campaign with settled funds to request a payout." testid="payout-no-campaigns" />
        ) : (
          <>
            <div className="space-y-4">
              <div>
                <Label>Campaign</Label>
                <Select value={campaignId} onValueChange={setCampaignId}>
                  <SelectTrigger className="mt-1.5" data-testid="payout-campaign-select"><SelectValue placeholder="Select a live campaign" /></SelectTrigger>
                  <SelectContent>
                    {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.title} — {money(c.amount_raised)} available</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {selected && (
                <div className="rounded-lg border border-ff-border bg-[#F3F4F6] p-3 text-sm text-ff-dark">
                  Available balance: <span className="font-bold text-ff-blue">{money(selected.amount_raised)}</span>
                </div>
              )}
              <div>
                <Label htmlFor="pa">Amount to request</Label>
                <div className="relative mt-1.5">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 font-bold text-ff-medium">$</span>
                  <Input id="pa" type="number" min="1" data-testid="payout-amount" value={amount}
                    onChange={(e) => setAmount(e.target.value)} className="pl-7" />
                </div>
              </div>

              {amt > 0 && (
                <div className="rounded-lg border border-ff-border bg-white p-4 text-sm" data-testid="payout-fee-preview">
                  <div className="flex items-center justify-between text-ff-dark">
                    <span>Requested amount</span><span className="font-semibold">{money(amt)}</span>
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-ff-medium">
                    <span className="flex items-center gap-1.5">
                      Platform fee ({feePct}%)
                      {fees?.is_pro
                        ? <span className="rounded-full bg-[#FEF3C7] px-1.5 py-0.5 text-[10px] font-extrabold text-[#92400E]">PRO RATE</span>
                        : fees && <span className="text-[11px] text-ff-medium">Pro pays {fees.pro_fee_pct}%</span>}
                    </span>
                    <span className="font-semibold text-ff-red" data-testid="payout-fee-amount">− {money(feeAmount)}</span>
                  </div>
                  <div className="mt-2 flex items-center justify-between border-t border-ff-border pt-2">
                    <span className="font-bold text-ff-blue">You receive</span>
                    <span className="font-heading text-lg font-extrabold text-ff-green" data-testid="payout-net-amount">{money(netAmount)}</span>
                  </div>
                  {fees && !fees.is_pro && (
                    <Link to="/nonprofit/dashboard" data-testid="payout-pro-upsell" className="mt-2 block text-xs font-semibold text-ff-blue underline">
                      Upgrade to Pro and keep {money(Math.round(amt * (fees.platform_fee_pct - fees.pro_fee_pct)) / 100)} more on this payout →
                    </Link>
                  )}
                </div>
              )}
            </div>
            <Button type="submit" disabled={loading || !onboardingComplete} data-testid="payout-submit"
              className="mt-6 w-full bg-ff-action font-semibold text-white hover:bg-ff-actionDark">
              {loading ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Requesting</> : <><Banknote className="mr-2 h-4 w-4" /> Request payout</>}
            </Button>
          </>
        )}
      </form>

      <h2 className="mt-10 mb-3 font-heading text-lg font-bold text-ff-blue">Payout history</h2>
      {payouts.length === 0 ? (
        <div className="rounded-xl border border-dashed border-ff-border bg-white p-6 text-center text-sm text-ff-dark">No payout requests yet.</div>
      ) : (
        <div className="space-y-3" data-testid="payout-history">
          {payouts.map((p) => (
            <div key={p.id} className="rounded-xl border border-ff-border bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-heading font-bold text-ff-blue">{money(p.amount)}</div>
                  <div className="text-xs text-ff-medium">{p.campaign_title}</div>
                </div>
                <StatusPill status={p.status} />
              </div>
              {(p.fee_amount !== undefined && p.fee_amount !== null) && (
                <div className="mt-2 flex items-center gap-3 text-xs text-ff-medium" data-testid="payout-history-fee">
                  <span>Fee {p.fee_pct}%: <span className="font-semibold text-ff-red">− {money(p.fee_amount)}</span></span>
                  <span>Net: <span className="font-semibold text-ff-green">{money(p.net_amount)}</span></span>
                  {p.is_pro_payout && <span className="rounded-full bg-[#FEF3C7] px-1.5 py-0.5 text-[10px] font-extrabold text-[#92400E]">PRO</span>}
                </div>
              )}
              {p.eligibility_reasons?.length > 0 && (
                <div className="mt-2 flex items-start gap-2 text-xs text-ff-amber">
                  <Clock className="mt-0.5 h-3.5 w-3.5" /> {p.eligibility_reasons.join(" · ")}
                </div>
              )}
              {p.status === "released" && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-ff-green"><CheckCircle2 className="h-3.5 w-3.5" /> Funds released via Stripe</div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
